import { useState, useContext, useEffect } from "react";
import { useLocation, useHistory } from "react-router";
import { Container, Row } from "react-bootstrap";
import LeftContent from "../components/authentication/LeftContent";
import LoginForm from "../components/authentication/LoginForm";
import EnterMFACode from "../components/authentication/EnterMFACode";
import { useAuth } from "context/auth";
import authService, {
    AuthMFAResponse,
    TokenResponse,
    LoginSession,
} from "service/authenticationService";
import AccountService from "service/accountService";
import {
    getLatestSubscription,
    getSubscriptionInfo,
} from "service/subscriptionService";
import { getAllTokens, isHttpSuccess } from "utils/functions";
import LocalStorageService from "service/localStorageService";
import AxiosInstance from "service/axiosInstance";
import { AppContext } from "context/appContext";
import {
    changeOrganizationAction,
    verifyOrgOwnerAction,
    updateIsUserOrgLoadedAction,
    storeUserAction,
    storeTokenBalanceAction,
    updateSubscriptionStatus,
    storeOrgMembersAction,
    storeOrgGroupsAction,
    storeOrgRolesAction,
    storeOrganizationAction,
} from "store/actions";
import {
    getAllJSON,
    fetchMetricInfo,
    getTimezoneList,
} from "service/jsonService";
import {
    HttpStatus,
    SecondsToCountdown,
    SEND_VERIFY_CODE_SUCCESS_MESSAGE,
    SubscriptionStatus,
} from "constant";
import { OrganisationDto } from "types";
import { fetchOrgList } from "utils/organisationFunction";
import { showErrorAlert, showSuccessAlert } from "utils/alert";
import { useSubscriptionLocalStorage } from "hooks/useSubscriptionLocalStorage";

const Home = () => {
    const { storeDispatchActions } = useContext(AppContext);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isError, setIsError] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoginWithPhoneNumber, setIsLoginWithPhoneNumber] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const {
        context: { setAuthTokens },
    } = useAuth();
    const [errorMessage, setErrorMessage] = useState("");
    const [isMFAEnable, setIsMFAEnable] = useState(false);
    const [session, setSession] = useState<LoginSession>();
    const location: any = useLocation();
    const history: any = useHistory();
    const { setLocalStorageSubscription }: any = useSubscriptionLocalStorage();

    useEffect(() => {
        if (
            window.location.pathname === "/callback" &&
            window.location.search === "?session=expired"
        ) {
            showErrorAlert({
                title: "Session expired!",
                message: "Your session has expired. Please login again.",
            });
        }
    }, []);

    const [countdown, setCountdown] = useState(SecondsToCountdown);

    const getSubscriptionStatus = async () => {
        const subscriptionRes: any = await getSubscriptionInfo();
        if (isHttpSuccess(subscriptionRes.status)) {
            for (const subscription of subscriptionRes.data) {
                if (
                    subscription.type_ === "SUBSCRIPTION" &&
                    [
                        SubscriptionStatus.ACTIVE,
                        SubscriptionStatus.CANCELED,
                        SubscriptionStatus.PENDING,
                    ].includes(subscription.status)
                ) {
                    storeDispatchActions(
                        updateSubscriptionStatus({
                            isSubscribed: true,
                            hasSubscribed: true,
                        })
                    );
                    return;
                } else {
                    storeDispatchActions(
                        updateSubscriptionStatus({
                            isSubscribed: false,
                            hasSubscribed: true,
                        })
                    );
                }
            }
        }
    };

    const getTokenBalance = async () => {
        const total: any = await getAllTokens();
        storeDispatchActions(
            storeTokenBalanceAction({ tokenBalance: total, isLoaded: true })
        );
    };

    const getUserOrgs = async (callback: () => void) => {
        const orgList = await fetchOrgList();

        let orgId =
            LocalStorageService.getItem("lastLoggedOrgId") ??
            LocalStorageService.getItem("orgId");

        const currentOrg =
            orgList.find((org: OrganisationDto) => org.uuid === orgId) ||
            orgList.find((org: OrganisationDto) => org.isDefault);

        if (currentOrg) {
            orgId = currentOrg.uuid;

            LocalStorageService.setItem("orgId", orgId);
            LocalStorageService.removeItem("lastLoggedOrgId");

            storeDispatchActions(storeOrganizationAction({ orgList }));

            storeDispatchActions(
                changeOrganizationAction({
                    orgInfo: currentOrg,
                })
            );
            storeDispatchActions(
                updateIsUserOrgLoadedAction({ isLoaded: true })
            );
            storeDispatchActions(
                verifyOrgOwnerAction({
                    isOwner: email === currentOrg.owner,
                })
            );

            storeDispatchActions(
                storeOrgMembersAction({
                    orgMembers: currentOrg.members,
                })
            );

            storeDispatchActions(
                storeOrgGroupsAction({
                    orgGroups: currentOrg.groups,
                })
            );

            storeDispatchActions(
                storeOrgRolesAction({
                    orgRoles: currentOrg.roles,
                })
            );

            setIsLoggedIn(true);
            setIsLoading(false);

            callback();
        } else {
            alert(
                "You do not have primary organisation, please contact Technical support."
            );
        }
    };

    const loginValidation = () => {
        let isValid = true;

        if (!password.trim() || !email.trim()) isValid = false;

        return isValid;
    };

    const handleLogin = async (resend?: any) => {
        if (!loginValidation()) return;

        setIsLoading(true);
        try {
            let _email = email.toLocaleLowerCase();
            const response = await authService.login({
                email: _email,
                password: password,
            });
            const { status, data, error } = response;

            if (!isHttpSuccess(status)) {
                setIsLoading(false);
                setIsError(true);

                if (error?.code === "NetworkError") {
                    showErrorAlert({
                        title: 'No internet connection',
                        message: 'Please check your internet connection and try again.',
                    });
                    return;
                }

                if (
                    status === HttpStatus.BAD_REQUEST &&
                    error?.message === "Password attempts exceeded"
                ) {
                    setErrorMessage(
                        "You've exceeded the maximum number of password attempts. Please try again after some time."
                    );
                    return;
                }

                setErrorMessage("Incorrect username or password.");
                return;
            }

            const { challengeName, session } = data as AuthMFAResponse;
            if (challengeName === "SMS_MFA") {
                if (resend) {
                    showSuccessAlert({
                        title: `New Code Sent`,
                        message: SEND_VERIFY_CODE_SUCCESS_MESSAGE,
                    });
                }
                setIsLoading(false);
                setIsError(false);
                setErrorMessage("");
                setEmail(_email);
                setSession(session);
                setCountdown(SecondsToCountdown);
                setIsMFAEnable(true);
                return;
            }

            const { AccessToken } = data as TokenResponse;
            AxiosInstance.defaults.headers.common[
                "Authorization"
            ] = `Bearer ${AccessToken}`;

            if (isLoginWithPhoneNumber) {
                const userRes = await AccountService.getAccountInfo();

                if (isHttpSuccess(userRes.status)) {
                    _email = userRes.data.email;
                    storeDispatchActions(
                        storeUserAction({
                            userData: userRes.data,
                            isLoaded: true,
                        })
                    );
                } else {
                    throw Error(`${userRes.status}`);
                }
            }
            getAllJSON();
            fetchMetricInfo();
            getTimezoneList();
            getUserOrgs(() => {
                setAuthTokens(data);
                window.loadAuthorizationConfig();
                if (
                    location.state?.referer?.pathname ===
                        "/billing-history" &&
                    location.state.referer.search &&
                    !location.state.referer.search.includes("callback")
                ) {
                    history.push(
                        `/billing-history?callback&${location.state.referer.search.slice(
                            1
                        )}`
                    );
                }
            });
            getTokenBalance();
            const subscription = await getLatestSubscription();
            setLocalStorageSubscription(subscription);
            getSubscriptionStatus();
        } catch (error: any) {
            setIsLoading(false);
            setIsError(true);

            // Not expect to happen, but handle it based on the old code
            setErrorMessage("Incorrect username or password.");
        }
    };

    const handelSubmitMFACode = async (code: string) => {
        if (!session) return;

        setErrorMessage("");
        const { status, data, error } = await authService.submitMFACode(
            session,
            code
        );
        if (isHttpSuccess(status)) {
            const { AccessToken } = data as TokenResponse;
            AxiosInstance.defaults.headers.common[
                "Authorization"
            ] = `Bearer ${AccessToken}`;

            await AccountService.getAccountInfo();

            getUserOrgs(() => {
                setAuthTokens(data);
            });
        } else {
            setErrorMessage(
                error?.message || " Invalid OTP code. Please try again."
            );
        }
    };

    const handleBackToLogin = () => {
        setIsError(false);
        setErrorMessage("");
        setIsMFAEnable(false);
    };

    const DesktopComponent = (
        <section>
            <Container fluid>
                <Row className="flex-row-reverse">
                    {!isMFAEnable ? (
                        <LoginForm
                            handleLogin={handleLogin}
                            isLoading={isLoading}
                            isLoggedIn={isLoggedIn}
                            setEmail={setEmail}
                            setPassword={setPassword}
                            setIsLoginWithPhoneNumber={
                                setIsLoginWithPhoneNumber
                            }
                            isError={isError}
                            errorMessage={errorMessage}
                        />
                    ) : (
                        <EnterMFACode
                            onSubmit={handelSubmitMFACode}
                            onBack={handleBackToLogin}
                            errorMessage={errorMessage}
                            handleLogin={handleLogin}
                            countdown={countdown}
                            setCountdown={setCountdown}
                        />
                    )}
                    <LeftContent></LeftContent>
                </Row>
            </Container>
        </section>
    );

    return DesktopComponent;
};

export default Home;
